// runtime.js: Lua runtime init for the browser (plan-m1.md v2 Change #5).
//
// v2 corrections from M1-1 spike:
//   - Does NOT override math.random. Native Lua 5.4 RNG is deterministic with
//     fixed seed (M1-1 spike confirmed); custom Xoshiro256** is unnecessary
//     unless browser-vs-Node parity test fails in M1-9.
//   - Asserts lua._VERSION === "Lua 5.4" on init; fails loudly if not.
//   - Returns minimal { engine } in M1; M2 extends with seed + game loaders.
//
// WASM URL: strategy (c), explicit `/glue.wasm` path served from client/public/
// (kept in sync by `npm run sync:wasm`, runs automatically pre-dev / pre-build).

import { LuaFactory } from 'wasmoon';

const WASM_URL = '/glue.wasm';
const EXPECTED_LUA_VERSION = 'Lua 5.4';

export class LuaRuntimeError extends Error {
  constructor(message, cause) {
    super(message);
    this.name = 'LuaRuntimeError';
    if (cause !== undefined) this.cause = cause;
  }
}

/**
 * Initialize wasmoon and assert Lua 5.4 semantics.
 * @returns {Promise<{ engine: import('wasmoon').LuaEngine }>}
 */
export async function createLuaRuntime() {
  let engine;
  try {
    const factory = new LuaFactory(WASM_URL);
    engine = await factory.createEngine();
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    throw new LuaRuntimeError(`wasmoon createEngine failed: ${msg}`, err);
  }

  // AC #5: verify _VERSION matches expectation; fail loudly otherwise
  let version;
  try {
    version = await engine.doString('return _VERSION');
  } catch (err) {
    await engine.global.close();
    const msg = err instanceof Error ? err.message : String(err);
    throw new LuaRuntimeError(`Lua _VERSION query failed: ${msg}`, err);
  }

  if (version !== EXPECTED_LUA_VERSION) {
    await engine.global.close();
    throw new LuaRuntimeError(
      `unexpected Lua version: got "${version}", expected "${EXPECTED_LUA_VERSION}". Stack assumes Lua 5.4 semantics.`
    );
  }

  // Log version in dev only (no-console rule in production)
  if (import.meta.env?.DEV) {
    // eslint-disable-next-line no-console
    console.info(`[lua] runtime: ${version}`);
  }

  return { engine };
}
